import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { staggeredBaseQuery } from "./staggeredBaseQuery";
import {
  Evaluation,
  EvaluationInsightViews,
  EvaluationMetadata,
  EvaluationArchiveViews,
  MarinerEvaluationInsightsView,
  EvaluationAssessor,
} from "../../common/Evaluation";

export const evaluationApi = createApi({
  reducerPath: "evaluationApi",
  tagTypes: ["Evaluation", "EvaluationAssessor"],
  baseQuery: staggeredBaseQuery(""),
  endpoints: (builder) => ({
    getEvaluation: builder.query<Evaluation, String>({
      query: (id) => {
        return {
          method: "GET",
          url: `evaluation/${id}`,
        };
      },
    }),
    updateEvaluation: builder.mutation<
      Evaluation,
      { id: string; evaluation: Partial<Evaluation> }
    >({
      query: ({ id, evaluation }) => {
        return {
          method: "PUT",
          url: `evaluation/${id}`,
          body: evaluation,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "Evaluation", id: id },
      ],
    }),
    createEvaluationFromVesselAndProfile: builder.mutation<
      Evaluation,
      { vesselId: number; profileId: number }
    >({
      query: ({ vesselId, profileId }) => {
        let url = `evaluation/${!!vesselId ? "&vesselId=" + vesselId : ""}${
          !!profileId ? "&profileId=" + profileId : ""
        }
        `;
        url = url.replace("evaluation/&", "evaluation?");
        return {
          method: "POST",
          url,
        };
      },
      invalidatesTags: [{ type: "Evaluation", id: "LIST" }],
    }),
    createEvaluationFromDispatch: builder.mutation<
      Evaluation,
      { dispatchId: number }
    >({
      query: ({ dispatchId }) => ({
        method: "POST",
        url: `evaluation/dispatch?dispatchId=${dispatchId}`,
      }),
      invalidatesTags: [{ type: "Evaluation", id: "LIST" }],
    }),
    addSignature: builder.mutation<Evaluation, { id: string; file: FormData }>({
      query: ({ id, file }) => {
        return {
          method: "PATCH",
          url: `evaluation/${id}`,
          body: file,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "Evaluation", id: id },
      ],
    }),
    submitEvaluation: builder.mutation<
      Evaluation,
      { id: string; file?: FormData }
    >({
      query: ({ id, file }) => {
        return {
          method: "POST",
          url: `evaluation/${id}/submit`,
          body: file,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "Evaluation", id: id },
      ],
    }),
    approveEvaluation: builder.mutation<
      Evaluation,
      { id: string; file?: FormData }
    >({
      query: ({ id, file }) => {
        return {
          method: "POST",
          url: `evaluation/${id}/approve`,
          body: file,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "Evaluation", id: id },
      ],
    }),
    rejectEvaluation: builder.mutation<
      Evaluation,
      { id: string; note: FormData }
    >({
      query: ({ id, note }) => {
        return {
          method: "POST",
          url: `evaluation/${id}/reject`,
          body: note,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "Evaluation", id: id },
      ],
    }),
    archiveEvaluation: builder.mutation<Evaluation, { id: string }>({
      query: ({ id }) => {
        return {
          method: "POST",
          url: `evaluation/archiveEvaluation?evaluationId=${id}`,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "Evaluation", id: id },
      ],
    }),
    getEvaluationMetadata: builder.query<
      EvaluationMetadata,
      { fleetId?: number; vesselId?: number }
    >({
      query: ({ fleetId, vesselId }) => {
        const params = [];
        if (fleetId) params.push(`fleetId=${fleetId}`);
        if (vesselId) params.push(`vesselId=${vesselId}`);
        const queryString = params.length ? `?${params.join("&")}` : "";
        return {
          method: "GET",
          url: `evaluation/metadata${queryString}`,
        };
      },
    }),
    getEvaluationInsights: builder.query<
      EvaluationInsightViews,
      { fleetId?: number; vesselId?: number }
    >({
      query: ({ fleetId, vesselId }) => {
        const params = [];
        if (fleetId) params.push(`fleetId=${fleetId}`);
        if (vesselId) params.push(`vesselId=${vesselId}`);
        const queryString = params.length ? `?${params.join("&")}` : "";
        return {
          method: "GET",
          url: `evaluation/insights${queryString}`,
        };
      },
    }),
    getMarinerEvaluationInsights: builder.query<MarinerEvaluationInsightsView, {}>({
      query: () => {
        return {
          method: "GET",
          url: `evaluation/marinerInsights`,
        };
      },
    }),
    createMissingEvaluationsFromInsights: builder.mutation<
      null,
      { fleetId?: number; vesselId?: number }
    >({
      query: ({ fleetId, vesselId }) => {
        const params = [];
        if (fleetId) params.push(`fleetId=${fleetId}`);
        if (vesselId) params.push(`vesselId=${vesselId}`);
        const queryString = params.length ? `?${params.join("&")}` : "";
        return {
          method: "POST",
          url: `evaluation/missing${queryString}`,
        };
      },
      invalidatesTags: (result, error, { fleetId, vesselId }) => [
        { type: "Evaluation", id: fleetId || vesselId },
      ],
    }),
    getEvaluationArchives: builder.query<
      EvaluationArchiveViews,
      Partial<{
        fleetId: number | undefined;
        vesselId: number | undefined;
        startDate: string | undefined;
        endDate: string | undefined;
      }>
    >({
      query: ({ fleetId, vesselId, startDate, endDate }) => {
        return {
          method: "GET",
          url: `evaluation/archives?${!!fleetId ? `fleetId=${fleetId}&` : ""}${
            !!vesselId ? `vesselId=${vesselId}&` : ""}${
              !!startDate ? `afterDate=${startDate}&` : ""}${
                !!endDate ? `beforeDate=${endDate}&` : ""}`,
        };
      },
    }),
    getEvaluationByProfileId: builder.query<Evaluation[], String>({
      query: (id) => {
        return {
          method: "GET",
          url: `evaluation/profile/${id}`,
        };
      },
    }),
    getAssessors: builder.query<EvaluationAssessor[], { search: string, vesselId: number }>({
      query: ({
        search,
        vesselId
      }) => {
        return {
          method: "GET",
          url: `evaluation/assessors?vesselId=${vesselId}${search ? '&search=' + search : ''}`,
        };
      },
      providesTags: (result) =>
        result && result.length
          ? [
              ...result.map(({ userId }) => ({
                type: "EvaluationAssessor" as const,
                userId,
              })),
              { type: "EvaluationAssessor", id: "LIST" },
            ]
          : [{ type: "EvaluationAssessor", id: "LIST" }],
    }),
  }),
});

export const {
  useGetEvaluationQuery,
  useUpdateEvaluationMutation,
  useGetEvaluationByProfileIdQuery,
  useCreateEvaluationFromVesselAndProfileMutation,
  useCreateEvaluationFromDispatchMutation,
  useCreateMissingEvaluationsFromInsightsMutation,
  useAddSignatureMutation,
  useSubmitEvaluationMutation,
  useApproveEvaluationMutation,
  useRejectEvaluationMutation,
  useArchiveEvaluationMutation,
  useGetEvaluationMetadataQuery,
  useGetEvaluationInsightsQuery,
  useGetMarinerEvaluationInsightsQuery,
  useGetEvaluationArchivesQuery,
  useGetAssessorsQuery,
} = evaluationApi;
